import { useCallback, useEffect } from "react";
import { driver, DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import { useUserSitesByOrg } from "../../user/hooks/user.hooks";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next'
import store from "../../store/store"
import { useAppDispatch, useAppSelector } from "../../store/store.hooks";
import { setOnboardingCompleted } from "../../store/authSlice";
import { userService } from "../../api/services/user.service"

let activeDriverObj: ReturnType<typeof driver> | null = null

const PRODUCT_ROUTES = [
  { licenseName: 'WeSaw', link: '/WeSaw' },
  { licenseName: 'WePlane', link: '/WePlane' },
  { licenseName: 'WeTrack', link: '/WeTrack' },
  { licenseName: 'WeSchedule', link: '/WeSchedule' },
  { licenseName: 'WeDry', link: '/WeDry' },
]

const getSteps = (isMultiSite: boolean, hasProducts: boolean, t: (key: string) => string): DriveStep[] => [
{
    popover: {
      title: t('onboarding.welcome.title'),
      description: t('onboarding.welcome.description'),
      align: 'start',
    },
  },
    ...(isMultiSite ? [{
    element: '#organization-section',
    disableActiveInteraction: false,
    allowKeyboardControl: false,
    popover: {
      title: t('onboarding.hub.title'),
      description: t('onboarding.hub.description'),
      side: "top",
      align: 'center',
     showButtons: isMultiSite ? [] : ['next'],
    },
  } as DriveStep] : []),
  {
    element: '#help-topBar-button',
    popover: {
      title: t('onboarding.helpButton.title'),
      description: t('onboarding.helpButton.description'),
      side: "left",
      align: 'start',
    },
    disableActiveInteraction: true
  },
  {
    element: '#help-menu-popover',
    popover: {
      title: t('onboarding.helpMenu.title'),
      description: t('onboarding.helpMenu.description'),
      side: "bottom",
      align: 'center',
    },
    disableActiveInteraction: true
  },
  {
    element: '#breadcrumb',
    popover: {
      title: t('onboarding.breadcrumb.title'),
      description: t('onboarding.breadcrumb.description'),
      side: "bottom",
      align: 'center',
    },
    disableActiveInteraction: true
  },
   ...(isMultiSite ? [{
    element: '#breadcrumb-site',
    disableActiveInteraction: true,
    popover: {
      title: t('onboarding.breadcrumbSite.title'),
      description: t('onboarding.breadcrumbSite.description'),
      side: "top",
      align: 'center',
    },
  } as DriveStep] : []),
   {
    element: '#sidebar',
    popover: {
      title: t('onboarding.sideBar.title'),
      description: t('onboarding.sideBar.description'),
      side: "bottom",
      align: 'center',
    },
    disableActiveInteraction: true
  },
   {
    element: '#hamburger-button',
    popover: {
      title: t('onboarding.sideBarButton.title'),
      description: t('onboarding.sideBarButton.description'),
      side: "bottom",
      align: 'center',
    },
    disableActiveInteraction: true
  },
    ...(hasProducts ? [{
    element: '#product-section',
    disableActiveInteraction: true,
    popover: {
      title: t('onboarding.productSection.title'),
      description: t('onboarding.productSection.description'),
      side: "top",
      align: 'center',
    },
  } as DriveStep] : []),
     ...(hasProducts ? [{
    element: '#breadcrumb-product',
    disableActiveInteraction: true,
    popover: {
      title: t('onboarding.breadecrumbProduct.title'),
      description: t('onboarding.breadecrumbProduct.description'),
      side: "top",
      align: 'center',
    },
  } as DriveStep] : []),
  ...(hasProducts ? [{
    element: '#dashboards-navigation',
    disableActiveInteraction: true,
    popover: {
      title: t('onboarding.dashboardsNavigation.title'),
      description: t('onboarding.dashboardsNavigation.description'),
      side: "top",
      align: 'center',
    },
  } as DriveStep] : []),
   ...(hasProducts ? [{
    element: '#documentation-button',
    disableActiveInteraction: true,
    popover: {
      title: t('onboarding.documentationButton.title'),
      description: t('onboarding.documentationButton.description'),
      side: "top",
      align: 'center',
    },
  } as DriveStep] : []),
    ...(hasProducts ? [{
    element: '#reports-section',
    disableActiveInteraction: true,
    popover: {
      title: t('onboarding.reportsSection.title'),
      description: t('onboarding.reportsSection.description'),
      side: "top",
      align: 'center',
    },
  } as DriveStep] : []),
  {
    element: '#logo-container',
    popover: {
      title: t('onboarding.homeButton.title'),
      description: t('onboarding.homeButton.description'),
      side: "bottom",
      align: 'center',
    },
    disableActiveInteraction: true
  },
]

export function useOnboarding() {
const { t } = useTranslation()
  const sites = useUserSitesByOrg()
  const allSites = sites.flatMap(org => org.sites)
  const isMultiSite = allSites.length > 1
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const licenses = useAppSelector((state) => state.auth.productLicenses)
  const hasProducts = (licenses?.length ?? 0) > 0
  const onboardingCompleted = useAppSelector((state) => state.auth.userDetails?.onboardingCompleted)

 useEffect(() => {
  if (onboardingCompleted === undefined) return
  if (onboardingCompleted) return
  if (activeDriverObj?.isActive()) return

  let driverObj: ReturnType<typeof driver>;
  const steps = getSteps(isMultiSite, hasProducts, t)
  const productStepIndex = steps.findIndex(s => s.element === '#product-section')
  const helpButtonIndex = steps.findIndex(s => s.element === '#help-topBar-button')
  const helpMenuIndex = steps.findIndex(s => s.element === '#help-menu-popover')

  driverObj = driver({
    popoverClass: 'my-theme',
    showProgress: true,
    allowClose: true,
    steps,
    onNextClick: (_el, _step, { state }) => {
      if (state.activeIndex === 1 && isMultiSite) {
        const currentSite = store.getState().auth.currentSite
        if (!currentSite) return
        driverObj.moveNext()
      } else if (state.activeIndex === helpButtonIndex) {
        window.dispatchEvent(new CustomEvent('open-help-menu'))
        setTimeout(() => driverObj.moveNext(), 300)
      } else if (state.activeIndex === helpMenuIndex) {
        window.dispatchEvent(new CustomEvent('close-help-menu'))
        driverObj.moveNext()
      } else if (productStepIndex !== -1 && state.activeIndex === productStepIndex) {
        const licenseNames = store.getState().auth.productLicenses?.map(l => l.licenseName) ?? []
        const firstProduct = PRODUCT_ROUTES.find(p => licenseNames.includes(p.licenseName))
        if (firstProduct) {
          navigate(firstProduct.link)
          setTimeout(() => driverObj.moveNext(), 800)
        } else {
          driverObj.moveNext()
        }
      } else {
        driverObj.moveNext()
      }
    },
    onDestroyStarted: () => {
      activeDriverObj = null
      driverObj.destroy()
    },
  });
  activeDriverObj = driverObj

  const startTour = () => {
    driverObj.drive()
    dispatch(setOnboardingCompleted(true))
    userService.updateOnboardingCompleted(true).catch(() => {})
  }

  if (isMultiSite) {
    navigate('/hub')
    const timeout = setTimeout(startTour, 2000);

    const unsubscribe = store.subscribe(() => {
      const currentSite = store.getState().auth.currentSite
      if (currentSite && driverObj.isActive() && driverObj.getActiveIndex() === 1) {
        unsubscribe()
      setTimeout(() => driverObj.moveNext(), 800)
      }
    })

    return () => {
      clearTimeout(timeout)
      unsubscribe()
    }
  }

  const timeout = setTimeout(startTour, 1000);
  return () => clearTimeout(timeout);
}, [isMultiSite, onboardingCompleted, dispatch]);
}

export function useRestartOnboarding() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const licenses = useAppSelector((state) => state.auth.productLicenses)
  const hasProducts = (licenses?.length ?? 0) > 0

  return useCallback(() => {
    if (activeDriverObj?.isActive()) {
      activeDriverObj.destroy()
    }
    activeDriverObj = null

    const steps = getSteps(false, hasProducts, t)
    const productStepIndex = steps.findIndex(s => s.element === '#product-section')
    const helpButtonIndex = steps.findIndex(s => s.element === '#help-topBar-button')
    const helpMenuIndex = steps.findIndex(s => s.element === '#help-menu-popover')

    const driverObj = driver({
      popoverClass: 'my-theme',
      showProgress: true,
      allowClose: true,
      steps,
      onNextClick: (_el, _step, { state }) => {
        if (state.activeIndex === helpButtonIndex) {
          window.dispatchEvent(new CustomEvent('open-help-menu'))
          setTimeout(() => driverObj.moveNext(), 300)
        } else if (state.activeIndex === helpMenuIndex) {
          window.dispatchEvent(new CustomEvent('close-help-menu'))
          driverObj.moveNext()
        } else if (productStepIndex !== -1 && state.activeIndex === productStepIndex) {
          const licenseNames = store.getState().auth.productLicenses?.map(l => l.licenseName) ?? []
          const firstProduct = PRODUCT_ROUTES.find(p => licenseNames.includes(p.licenseName))
          if (firstProduct) {
            navigate(firstProduct.link)
            setTimeout(() => driverObj.moveNext(), 800)
          } else {
            driverObj.moveNext()
          }
        } else {
          driverObj.moveNext()
        }
      },
      onDestroyStarted: () => {
        activeDriverObj = null
        driverObj.destroy()
      },
    })

    activeDriverObj = driverObj
    driverObj.drive()
  }, [hasProducts, navigate, t])
}
